import "./styles.scss";

import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useCommunity } from "@contexts/CommunityContext";

import { useScreen } from 'Screen/ScreenContext';
import {
    IconArrowFork,
    IconBan,
    IconChevronDown,
    IconChevronLeft,
    IconClipboardList,
    IconCoins,
    IconCurrentLocation,
    IconLoader2,
    IconMenu2,
    IconPigMoney,
    IconPlugConnected,
    IconPuzzle,
    IconSparkles,
    IconTag,
    IconUsersGroup,
    IconX,
} from "@tabler/icons-react";

import { Button, Text } from "@fragments";
import { CommunityBanner } from "@components/Community";
import Popper from "@fragments/Popper";

import General from './tabs/General';
import Pricing from './tabs/Pricing';
import Coupons from './tabs/Coupons';
import Split from './tabs/Split';
import Pixel from './tabs/Pixel';
import Integrations from './tabs/Integrations';
import Forms from './tabs/Forms';
import Mentoring from './tabs/Mentoring';

import { ConfigHeaderContext } from './hooks/useConfigHeader';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import api from "@api";


export default function CommunityConfig() {
    let { tab, slug } = useParams();
    const navigate = useNavigate();

    const { t } = useTranslation('settings');

    const { loading: communityLoading, community, communities } = useCommunity();
    const { setFullWidth } = useScreen();

    useEffect(() => {
        setFullWidth(true);
        return () => setFullWidth(false);
    }, [setFullWidth]);

    const [loadingCommunity, setLoadingCommunity] = useState(true);
    const [info, setInfo] = useState(null);

    // Subpage-published header (see hooks/useConfigHeader) and the DOM node of
    // the card's action slot that ConfigHeaderActions portals into. The slot is
    // held in state (callback ref) so consumers re-render once it exists.
    const [pageHeader, setPageHeader] = useState(null);
    const [actionsSlot, setActionsSlot] = useState(null);
    const headerContext = useMemo(() => ({ setPageHeader, actionsSlot }), [actionsSlot]);

    const contentRef = useRef(null);
    const subheaderRef = useRef(null);

    // The fixed header sits over the scrolling content, so the tabs rows that
    // are themselves sticky inside a subpage must stack below it. Its height
    // depends on the actions a subpage mounts, so the content area publishes
    // the live height as a CSS variable the stylesheet composes into
    // `--config-subheader-offset`.
    useLayoutEffect(() => {
        const content = contentRef.current;
        const header = subheaderRef.current;
        if (!content || !header) return;

        const publish = () => {
            content.style.setProperty('--config-subheader-height', `${header.offsetHeight}px`);
        };
        publish();

        const observer = new ResizeObserver(publish);
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    const currentCommunity = community.getBySlug(slug);

    const ownerCommunities = communities?.filter(
        c => ['owner', 'admin', 'creator'].includes(c.role)
    ) ?? [];

    const items = [
        {
            title: t('nav.tabs.general.title'),
            subtitle: t('nav.tabs.general.subtitle'),
            url: "general",
            icon: <IconSparkles />,
            component: <General info={info} handleRefresh={() => setLoadingCommunity(true)} />,
            default: true
        },
        {
            title: t('nav.tabs.monetization.title'),
            subtitle: t('nav.tabs.monetization.subtitle'),
            icon: <IconCoins />,
            children: [
                {
                    title: t('nav.tabs.pricing.title'),
                    subtitle: t('nav.tabs.pricing.subtitle'),
                    url: "pricing",
                    icon: <IconPigMoney />,
                    component: <Pricing info={info} />
                },
                {
                    title: t('nav.tabs.coupons.title'),
                    subtitle: t('nav.tabs.coupons.subtitle'),
                    url: "coupons",
                    icon: <IconTag />,
                    component: <Coupons info={info} />
                },
                {
                    title: t('nav.tabs.split.title'),
                    subtitle: t('nav.tabs.split.subtitle'),
                    url: "split",
                    icon: <IconArrowFork />,
                    component: <Split info={info} />
                }
            ]
        },
        {
            title: t('nav.tabs.integrations.title'),
            subtitle: t('nav.tabs.integrations.subtitle'),
            icon: <IconPuzzle />,
            children: [
                {
                    title: t('nav.tabs.pixel.title'),
                    subtitle: t('nav.tabs.pixel.subtitle'),
                    url: "pixel",
                    icon: <IconCurrentLocation />,
                    component: <Pixel info={info} handleRefresh={() => setLoadingCommunity(true)} />
                },
                {
                    title: t('nav.tabs.externalPlatforms.title'),
                    subtitle: t('nav.tabs.externalPlatforms.subtitle'),
                    url: "integrations",
                    icon: <IconPlugConnected />,
                    component: <Integrations info={info} />
                }
            ]
        },
        {
            title: t('nav.tabs.forms.title'),
            subtitle: t('nav.tabs.forms.subtitle'),
            url: "forms",
            icon: <IconClipboardList />,
            component: <Forms />
        },
        // Mentoria — owner only (§6.1). Mirrors the Forms tab → list → detail nesting.
        ...(currentCommunity?.role === 'owner' ? [{
            title: t('nav.tabs.mentoring.title'),
            subtitle: t('nav.tabs.mentoring.subtitle'),
            url: "mentoring",
            icon: <IconUsersGroup />,
            component: <Mentoring info={info} handleRefresh={() => setLoadingCommunity(true)} />
        }] : [])
    ];

    // Groups only shape the sidebar — tab resolution runs over the flat list.
    const flatItems = items.flatMap(i => i.children ?? [i]);
    const current = flatItems.find(i => i.url === tab) || flatItems[0];

    const hasAccess = ['owner', 'admin', 'creator'].includes(currentCommunity?.role);

    console.log(currentCommunity)

    useEffect(() => {
        if (slug) {
            setLoadingCommunity(true);
            setInfo(null);
            setPageHeader(null);
        }
    }, [slug]);

    useEffect(() => {
        if (loadingCommunity && currentCommunity?.id) {
            handleLoadCommunity();
        }
    }, [currentCommunity, loadingCommunity]);


    async function handleLoadCommunity() {
        try {
            const { data } = await api.get(`/community?id=${currentCommunity.id}`);
            if (data?.id) setInfo({ ...data });
        } catch (error) {
            console.log(error);
        }
        setLoadingCommunity(false);
    }

    return (
        <ConfigHeaderContext.Provider value={headerContext}>
        <div className="config-community-page pb-0">
            <div className="current-config-page">
                <ConfigSidebar
                    items={items}
                    ownerCommunities={ownerCommunities}
                    currentSlug={slug}
                    currentTab={tab}
                />

                <div className="config-content" ref={contentRef}>
                    {/* Fixed page header — the chat screen's gradient-and-blur
                        bar. The subpage publishes its own title / subtitle /
                        icon through useConfigHeader (falling back to the
                        sidebar entry) and portals its actions into the slot. */}
                    <div className="config-subheader" ref={subheaderRef}>
                        {/* Back to the community, like the chat header's. */}
                        <Button
                            className="config-subheader__back"
                            icon={<IconChevronLeft />}
                            variant="rounded"
                            color="overlay"
                            size="small"
                            onClick={() => navigate(`/${slug}`)}
                            tooltip={t('common:actions.back')}
                            aria-label={t('common:actions.back')}
                            preventLoading
                        />

                        <span className="config-subheader__icon">
                            {pageHeader?.icon ?? current.icon}
                        </span>
                        <div className="config-subheader__identity">
                            <Text.h5 weight={500} contrast={9} className="config-subheader__title">
                                {pageHeader?.title ?? current.title}
                            </Text.h5>
                            {pageHeader?.subtitle && (
                                <Text.span contrast={6} className="config-subheader__subtitle">
                                    {pageHeader.subtitle}
                                </Text.span>
                            )}
                        </div>
                        <div ref={setActionsSlot} className="config-subheader__actions" />
                    </div>

                    <div className="config-tab-content">
                        {loadingCommunity ? (
                            <Loading />
                        ) : hasAccess ? (
                            current.component
                        ) : (
                            <NotFound />
                        )}
                    </div>
                </div>
            </div>
        </div>
        </ConfigHeaderContext.Provider>
    );
}

function ConfigSidebar({ items, ownerCommunities, currentSlug, currentTab }) {
    const { t } = useTranslation('settings');
    const popperRef = useRef(null);

    // ≤960px the sidebar collapses to an icon rail; `open` expands it as an
    // overlay (max 400px) above the tab content, with a backdrop. Both the
    // toggle and the backdrop only exist visually in that range (see styles).
    const [open, setOpen] = useState(false);
    const close = () => setOpen(false);

    const currentCommunity = ownerCommunities.find(c => c.slug === currentSlug);

    function handleSelectCommunity(c) {
        popperRef.current?.close();
        close();
        window.navigate(c.slug + '/settings/' + (currentTab || ''));
    }

    return (
        <>
        <div
            className={`ccs-backdrop${open ? ' is-visible' : ''}`}
            onClick={close}
        />
        <div className={`config-community-sidebar${open ? ' is-open' : ''}`}>
            <button
                type="button"
                className="ccs-toggle"
                onClick={() => setOpen(o => !o)}
                aria-label={open ? t('nav.closeMenu') : t('nav.openMenu')}
            >
                {open ? <IconX size={18} /> : <IconMenu2 size={18} />}
            </button>

            <div className="ccs-header">
                <Text.h5 contrast={9} weight={400}>
                    {t('nav.title')}
                </Text.h5>
            </div>

            <Popper
                controlRef={popperRef}
                config={{
                    searchKeyExtractor: (opt, validate) => validate(opt.label)
                }}
            >
                <div
                    className="ccs-selector"
                    onClick={(e) => {
                        const options = ownerCommunities.map(c => ({
                            id: c.id,
                            label: c.name,
                            variant: c.slug === currentSlug ? 'active' : 'default',
                            icon: (
                                <CommunityBanner
                                    small
                                    color="white"
                                    className="ccs-selector-banner"
                                    banner={c.banner}
                                />
                            ),
                            onClick: () => handleSelectCommunity(c)
                        }));
                        popperRef.current.open(options, 'bottom-start', e.currentTarget);
                    }}
                >
                    <div className="ccs-selector-trigger">
                        <CommunityBanner
                            size={30}
                            color="white"
                            className="ccs-selector-banner"
                            banner={currentCommunity?.banner}
                        />
                        <Text.h6 contrast={6} grow className="ccs-selector-name">
                            {currentCommunity?.name ?? '—'}
                        </Text.h6>
                        <IconChevronDown className="ccs-chevron" />
                    </div>
                </div>
            </Popper>

            <div className="ccs-menu mt-2">
                {items.map((item, index) => item.children ? (
                    <SidebarGroup
                        key={index}
                        {...item}
                        currentTab={currentTab}
                        currentSlug={currentSlug}
                        onNavigate={close}
                    />
                ) : (
                    <SidebarButton
                        key={index}
                        {...item}
                        currentTab={currentTab}
                        currentSlug={currentSlug}
                        onNavigate={close}
                    />
                ))}
            </div>
        </div>
        </>
    );
}

function SidebarGroup({ title, subtitle, icon, children, currentTab, currentSlug, onNavigate }) {
    const hasActiveChild = children.some(c => c.url === currentTab);
    const [expanded, setExpanded] = useState(hasActiveChild);

    // Landing on a child tab (deep link, community switch) must reveal it.
    useEffect(() => {
        if (hasActiveChild) setExpanded(true);
    }, [hasActiveChild]);

    return (
        <div className={`ccs-group${expanded ? ' is-expanded' : ''}`}>
            <div
                className={`ccs-menu-btn ccs-group-header${hasActiveChild && !expanded ? ' active' : ''}`}
                onClick={() => setExpanded(e => !e)}
                title={title}
            >
                <span className="ccs-menu-btn-icon">{icon}</span>
                <div className="ccs-menu-btn-desc">
                    <Text.h6 contrast={hasActiveChild ? 8 : 7} weight={400}>{title}</Text.h6>
                    <Text.span contrast={hasActiveChild ? 7 : 6}>{subtitle}</Text.span>
                </div>
                <IconChevronDown size={16} className="ccs-group-chevron" />
            </div>

            <div className="ccs-group-items">
                <div className="ccs-group-items-inner">
                    {children.map((item, index) => (
                        <SidebarButton
                            key={index}
                            {...item}
                            currentTab={currentTab}
                            currentSlug={currentSlug}
                            onNavigate={onNavigate}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function SidebarButton({ title, subtitle, icon, url, component, currentTab, currentSlug, onNavigate, ...props }) {
    const isActive = currentTab === url || (props.default && !currentTab);
    const isDisabled = !component;

    function handleClick() {
        if (isDisabled) return;
        onNavigate?.();
        window.navigate(currentSlug + '/settings/' + url);
    }

    return (
        <div
            className={`ccs-menu-btn ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
            onClick={handleClick}
            title={title}
        >
            <span className="ccs-menu-btn-icon">{icon}</span>
            <div className="ccs-menu-btn-desc">
                <Text.h6 contrast={isActive ? 8 : 7} weight={400}>{title}</Text.h6>
                <Text.span contrast={isActive ? 7 : 6}>{subtitle}</Text.span>
            </div>
        </div>
    );
}

function Loading() {
    const { t } = useTranslation('settings');

    return (
        <div className="config-loading">
            <IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            <Text.span contrast={5}>{t('common:actions.loading')}</Text.span>
        </div>
    );
}

function NotFound() {
    const { t } = useTranslation('settings');

    return (
        <div className="config-not-found">
            <IconBan size={36} />
            <Text.h2 contrast={3}>{t('nav.notFound')}</Text.h2>
        </div>
    );
}
